import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { appStateSliceName } from "./constants";
import { v4 } from "uuid";
import { shouldDisplay } from "./thunks/should-display";
import { getInitialMerchId } from "../../../application/utils/get-initial-merch-id";
import { getIpAddress } from "./thunks/get-ip-address";

export interface AppState {
  isOpen: boolean;
  suId: string;
  conversationId: string;
  sessionId: string;
  merchId: string;
  merchIdHash: string;
  botVersion: string;
  botType: string;
  isChatWebsocketConnected: boolean;
  isNudgeOpen: boolean;
  appInstancesCount: number;
  configFile?: string;
  hubspotMeetingScheduled?: boolean;
  isReadyHeartbeat?: boolean;
  shouldDisplay: boolean;
  callEnabled: boolean;
  ipAddress: string;
  isLANudge: boolean;
  visitorCountryCodeSource: string;
  visitorCountryCodeKey: string;
  visitorCountrySource: string;
  visitorCountryKey: string;
  heartBeatThreshold : number
}

const initialState: AppState = {
  isOpen: false,
  suId: v4(),
  conversationId: v4(),
  sessionId: v4(),
  merchId: getInitialMerchId(),
  merchIdHash: '',
  botVersion: 'v1-0',
  botType: 'WPA',
  isChatWebsocketConnected: false,
  isNudgeOpen: false,
  appInstancesCount: 0,
  configFile: 'frontend_config.json',
  hubspotMeetingScheduled: false,
  isReadyHeartbeat: false,
  shouldDisplay: false,
  ipAddress: '',
  callEnabled: false,
  isLANudge: false,
  visitorCountryCodeSource: '',
  visitorCountryCodeKey: '',
  visitorCountrySource: '',
  visitorCountryKey: '',
  heartBeatThreshold: 30000, //in ms
}

const localStorageKey = "voa_websocket_sessions_counter";

export const appStateSlice = createSlice({
  initialState,
  name: appStateSliceName,
  reducers: {
    resetAppState: (state) => {
      state.conversationId = v4();
      state.suId = v4();
      state.sessionId = v4();
      state.merchId = getInitialMerchId();
      state.isChatWebsocketConnected = false;
      state.isNudgeOpen = true;
      state.isOpen = false;
      state.hubspotMeetingScheduled = false;
    },
    setIsOpen: (state, action: PayloadAction<boolean>) => {
      const { payload } = action;

      state.isOpen = payload;
    },
    setHeartbeat: (state, action: PayloadAction<boolean>) => {
      const { payload } = action;

      state.isReadyHeartbeat = payload;
    },
    setHeartbeatThreshold: (state, action: PayloadAction<number>) => {
      const { payload } = action;
      state.heartBeatThreshold = payload;
    },
    setIsNudgeOpen: (state, action: PayloadAction<boolean>) => {
      const { payload } = action;

      state.isNudgeOpen = payload;
    },
    setIsChatWebsocketConnected: (state, action: PayloadAction<boolean>) => {
      const { payload } = action;

      state.isChatWebsocketConnected = payload;
    },
    setSessionId: (state, action: PayloadAction<string>) => {
      const { payload } = action;

      state.sessionId = payload;
    },
    incrementInstancesCount: (state) => {
      const prevValue = Number(localStorage.getItem(localStorageKey) || 0);

      localStorage.setItem(localStorageKey, String(prevValue + 1));
      state.appInstancesCount = prevValue + 1;
    },
    decrementInstancesCount: (state) => {
      const prevValue = Number(localStorage.getItem(localStorageKey) || 1);

      localStorage.setItem(localStorageKey, String(prevValue - 1));
      state.appInstancesCount = prevValue - 1;
    },
    setHubSpotMeetingScheduled: (state, action: PayloadAction<boolean>) => {
      const { payload } = action;

      state.hubspotMeetingScheduled = payload;
    },
    setIsLANudge: (state, action: PayloadAction<boolean>) => {
      const { payload } = action;

      state.isLANudge = payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(shouldDisplay.fulfilled, (state, action) => {
      state.isOpen = Boolean(state.isOpen || action.payload?.auto_open);
      state.shouldDisplay = action.payload ? action.payload.should_display : true;
      state.merchIdHash = action.payload?.merch_id_hash || '';
      state.callEnabled = !!action.payload?.live_agent_call_enabled;
      const countryCodeSourceAndKey = action.payload?.visitor_country_code_source_and_key || [];
      state.visitorCountryCodeSource = countryCodeSourceAndKey[0] || '';
      state.visitorCountryCodeKey = countryCodeSourceAndKey[1] || '';
      const countrySourceAndKey = action.payload?.visitor_country_source_and_key || [];
      state.visitorCountrySource = countrySourceAndKey[0] || '';
      state.visitorCountryKey = countrySourceAndKey[1] || '';
    });
    builder.addCase(getIpAddress.fulfilled, (state, action) => {
      state.ipAddress = action.payload;
    });
    builder.addCase(getIpAddress.rejected, (state) => {
      state.ipAddress = '';
    });
  },
});

export const {
  setIsOpen,
  setIsChatWebsocketConnected,
  setIsNudgeOpen,
  setSessionId,
  incrementInstancesCount,
  decrementInstancesCount,
  setHubSpotMeetingScheduled,
  setHeartbeat,
  resetAppState,
  setIsLANudge,
  setHeartbeatThreshold,
} = appStateSlice.actions;
